import type { ReactElement } from 'react';
import { Clock as ClockIcon } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import { Icon, type IconSize } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { formatCountdown } from '../utils/formatCountdown';

import styles from './PriceTimer.module.scss';

export type PriceTimerSize = 'xs' | 'sm' | 'base' | 'md' | 'lg';

export interface PriceTimerProps {
    /** Seconds left; shown as `hh:mm:ss`. */
    secondsLeft: number;
    /** Text before the time (translated). */
    label?: string;
    /** @default 'sm' */
    size?: PriceTimerSize;
    className?: string;
}

const ICON_SIZE: Record<PriceTimerSize, IconSize> = {
    xs: 'sm',
    sm: 'sm',
    base: 'md',
    md: 'lg',
    lg: 'xl',
};

/**
 * A countdown to the end of a price offer, with a clock icon.
 *
 * @example
 * <PriceTimer secondsLeft={secondsLeft} label={t('offer.price-timer.label')} />
 */
export function PriceTimer({ secondsLeft, label, size = 'sm', className }: PriceTimerProps): ReactElement {
    return (
        <Text
            as="span"
            size={size}
            color="accent"
            weight="medium"
            className={classNames(styles.timer, size === 'lg' && styles['timer-lg'], className)}
        >
            <Icon icon={ClockIcon} size={ICON_SIZE[size]} />
            {label && <span>{label}</span>}
            <span className={styles.time}>{formatCountdown(secondsLeft)}</span>
        </Text>
    );
}
